import React, {forwardRef} from 'react';
import {Modal} from 'react-bootstrap';
import I18n from '../i18n';
import Button from '../button';
import useTimeout from '../../hooks/use-timeout';
import useIdentity from '../../hooks/use-identity';
import config from '../../config';

function SessionTimeoutModalImpl(props, ref) {
  const identity = useIdentity();
  const [show, setShow] = React.useState();
  const sessionWarningDuration = 1000 * 60 * 5;
  const sessionWarningTimeout = useTimeout(() => setShow(true));
  const sessionSignOutTimeout = useTimeout(() => signOut());

  React.useImperativeHandle(ref, () => ({
    resetSessionTimers: () => {
      clearSessionTimers();
      setSessionTimers();
    },
  }));

  const renewSession = React.useCallback(async () => {
    clearSessionTimers();
    setSessionTimers();
    await identity.getToken({});
    setShow(false);
  });

  const signOut = React.useCallback(async () => {
    clearSessionTimers();
    await identity.logout();
    setShow(false);
  });

  const clearSessionTimers = React.useCallback(() => {
    sessionWarningTimeout.stop();
    sessionSignOutTimeout.stop();
  });

  const setSessionTimers = React.useCallback(() => {
    if (identity.isAuthenticated) {
      const expiration = config.sessionIdleTimeoutMinutes * 60 * 1000;
      sessionWarningTimeout.start(expiration - sessionWarningDuration);
      sessionSignOutTimeout.start(expiration);
    }
  });

  React.useEffect(() => {
    clearSessionTimers();
    setSessionTimers();

    if (!identity.isAuthenticated) {
      setShow(false);
    }

    return clearSessionTimers;
  }, [identity]);

  return (
    <Modal
      dialogClassName="session-timeout-modal modal-danger"
      backdropClassName="session-timeout-modal-backdrop"
      show={show}
      backdrop="static"
      size="lg"
      centered
    >
      <Modal.Header>
        <Modal.Title>
          <I18n t="session-timeout-modal.title" />
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <I18n t="session-timeout-modal.description" />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-dark" onClick={signOut}>
          <I18n t="action.logout" />
        </Button>
        <Button variant="success" onClick={renewSession}>
          <I18n t="session-timeout-modal.renew-session" />
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

const SessionTimeoutModal = forwardRef(SessionTimeoutModalImpl);
export default SessionTimeoutModal;
